import {inject, injectable} from 'inversify';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import type {ApiKeySummary} from 'xconvert-web-shared';

import {ResponsePipe} from '../utils/request.utils';
import {ApiConfig} from './api-config';
import {LightHttp} from './light-http.service';
import {LoginService} from './login.service';

/**
 * Portal-side service for managing the signed-in user's
 * personal ApiKeys per developer-api.md §14 E1 + E3.
 *
 * Wraps the portal routes exposed on professorx
 * (`/v1/api/portal/api-keys`). All calls require an
 * authenticated session; unauthenticated calls reject up
 * front before the network round-trip.
 *
 * The service is a thin RPC wrapper — it forwards the
 * server's [ApiKeySummary] shape (imported from
 * `xconvert-web-shared`, §14 E4) without local massaging so
 * the UI component sees the same field names as the public
 * API response.
 */
@injectable()
export class ApiKeysService {
    constructor(
        @inject(ApiConfig) private apiConfig: ApiConfig,
        @inject(LightHttp) private http: LightHttp,
        @inject(LoginService) private loginService: LoginService,
    ) {}

    /**
     * List the current user's keys per §5.1.
     *
     * `includeRevoked=false` (default) hides soft-revoked keys
     * so the default "My API keys" view stays uncluttered.
     * The portal UI exposes an "Include revoked" toggle that
     * bumps the query to `true`.
     */
    list(includeRevoked = false): Observable<ApiKeySummary[]> {
        this.requireLogin();
        const url = `${this.apiConfig.getApiKeysEndpoint()}?includeRevoked=${includeRevoked ? 'true' : 'false'}`;
        return this.http.get<{keys: ApiKeySummary[]}>(url).pipe(
            ResponsePipe,
            map(r => r?.keys ?? []),
        );
    }

    /**
     * Create a new ApiKey per §5.1 + §14 B2.
     *
     * Returns the one-shot response including the FULL
     * secret token (`secret: "xck_live_..."`). The portal UI
     * displays the secret ONCE (copyable-once pattern per
     * §14 E1 acceptance criteria) and then shows only the
     * id-suffixed summary in subsequent renders.
     *
     * `preset` mirrors the server's scope shortcuts:
     * - `read-only` → `jobs:read`, `features:read`,
     *   `keys:read`, `webhooks:read`, `account:read`.
     * - `full` → every non-admin scope.
     *
     * For custom scope sets the caller can omit `preset`
     * and pass `scopes` explicitly; the server validates.
     */
    create(input: {
        name: string;
        preset?: 'read-only' | 'full';
        scopes?: string[];
        expiresAt?: string;
        allowedIpRanges?: string[];
    }): Observable<ApiKeyCreateResponse> {
        this.requireLogin();
        if (!input.name || input.name.trim().length === 0) {
            throw new Error('ApiKey name is required');
        }
        if (!input.preset && (!input.scopes || input.scopes.length === 0)) {
            throw new Error('ApiKey preset or explicit scopes[] is required');
        }
        return this.http.post<ApiKeyCreateResponse>(this.apiConfig.getApiKeysEndpoint(), input).pipe(
            ResponsePipe,
        );
    }

    /**
     * Soft-revoke a key per §5.1. Subsequent API calls from
     * the key return `401 key_revoked`. Revocation is
     * irreversible — a new key must be created if access is
     * needed again.
     */
    revoke(id: string, reason?: string): Observable<ApiKeySummary> {
        this.requireLogin();
        return this.http.post<ApiKeySummary>(this.apiConfig.getApiKeyRevokeEndpoint(id), {
            reason: reason ?? 'user_revoked',
        }).pipe(ResponsePipe);
    }

    /**
     * Rotate the secret on an existing key per §5.1.4.
     * Returns the same one-shot response as [create] with
     * the NEW secret string; the old secret remains valid
     * for the 24h overlap window per §5.5.4.
     */
    rotate(id: string): Observable<ApiKeyCreateResponse> {
        this.requireLogin();
        return this.http.post<ApiKeyCreateResponse>(this.apiConfig.getApiKeyRotateEndpoint(id), {}).pipe(
            ResponsePipe,
        );
    }

    /**
     * Get a single key's summary (no secret).
     */
    get(id: string): Observable<ApiKeySummary> {
        this.requireLogin();
        return this.http.get<ApiKeySummary>(this.apiConfig.getApiKeyByIdEndpoint(id)).pipe(ResponsePipe);
    }

    private requireLogin() {
        if (!this.loginService.isUserLoggedIn) {
            throw new Error('ApiKeysService: user must be logged in');
        }
    }
}

/**
 * One-shot response from `POST /portal/api-keys` and
 * `POST /portal/api-keys/{id}/rotate`. The `secret` is
 * returned EXACTLY ONCE — the portal must surface it to the
 * user immediately and clear it from memory after.
 */
export interface ApiKeyCreateResponse {
    /** The newly-minted summary (never includes the secret). */
    apiKey: ApiKeySummary;

    /**
     * The full secret token (e.g. `xck_live_abcd...`). NEVER
     * returned again after this initial response — not in
     * GET, not in list, not on rotate. The portal UI displays
     * this once with a copy button and a "save this now"
     * warning.
     */
    secret: string;
}
